/**
 * MetaAgentModelsPanel — super-admin UI for the per-tenant models of the
 * meta-agents (Concierge + Agent of Agents), which have no chatbot row.
 *
 * For each agent the super-admin sets:
 *   - default_model  → what the agent uses by default.
 *   - allowed_models → what a user may pick in the typing bar ([] = anything).
 *
 * Both hold a **ref**, which is one of two things:
 *
 *   • a FAMILY key ("sonnet") — the agent follows whatever release that family
 *     is pinned to for this tenant, and moves when the pin moves;
 *   • a concrete RELEASE id ("claude-sonnet-4-6") — the agent runs exactly
 *     that release, whatever the family pin says.
 *
 * This panel used to offer families only, and normalized any stored release
 * down to its family on load. That meant the 28 releases selectable in the
 * Model families tab were not selectable here — the operator's complaint, and
 * the reason the normalization is gone (2026-09-11).
 *
 * Storage: tenant.tenant_settings[<agent>] = { default_model, allowed_models }.
 * Data source: GET/PATCH /super-admin/tenants/<id>/meta-agent-models, which
 * returns the family catalog resolved for THIS tenant plus every release in
 * each family. Adding a model to config/ai_models.json surfaces it here with
 * no code change.
 *
 * Cascade toolbar (CascadeControls, domain 'meta_agents'): the same
 * apply-to-sub-tenants / enforce controls the Model families tab has. Every
 * PATCH here runs the same bookkeeping — a direct edit on an enforcing tenant
 * re-pushes the slots down (toast), a direct edit on a descendant clears the
 * ancestor's enforcement (warning) — and the GET carries `cascade`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Select, Spin, Empty, message as antdMessage, Space, Tag } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { CascadeControls, CascadeStatus } from './CascadeControls';

interface ReleaseEntry {
  id: string;
  name: string;
  /** True for the release this family currently points at for this tenant. */
  is_family_pin?: boolean;
}

interface FamilyEntry {
  id: string; // family key: 'sonnet', 'gpt-mini', ...
  kind?: string;
  name: string;
  provider: string;
  provider_name?: string;
  resolved_model?: string;
  resolved_model_name?: string;
  description?: string;
  enabled?: boolean;
  versions?: ReleaseEntry[];
}

interface AgentConfig {
  label: string;
  default_model: string; // family key OR release id
  allowed_models: string[]; // family keys and/or release ids; [] = anything
  is_default: boolean;
  platform_default: string;
}

interface MetaAgentModelsPanelProps {
  tenantId: string;
  /** Notify the parent page after any change that can move the tenant-tree
   *  badges (enforcement / differs-from-parent), so they refresh instead of
   *  showing yesterday's state until the tab is re-entered. */
  onCascadeChanged?: () => void;
}

const AGENT_ORDER = ['concierge', 'agent_of_agents', 'guide'] as const;

export function MetaAgentModelsPanel({ tenantId, onCascadeChanged }: MetaAgentModelsPanelProps) {
  const { t } = useLanguage();
  const [families, setFamilies] = useState<FamilyEntry[]>([]);
  const [agents, setAgents] = useState<Record<string, AgentConfig>>({});
  const [cascade, setCascade] = useState<CascadeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingAgent, setSavingAgent] = useState<string | null>(null);
  /** `${agentKey}:${familyKey}` for each family the user has opened. */
  const [openFamilies, setOpenFamilies] = useState<Set<string>>(new Set());

  const toggleFamilyOpen = (agentKey: string, familyKey: string) =>
    setOpenFamilies(prev => {
      const next = new Set(prev);
      const k = `${agentKey}:${familyKey}`;
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });

  // Used on mount and after every cascade action from CascadeControls: an
  // apply or an enforcement toggle moves the drift / enforced badges, and an
  // apply can rewrite this very tenant's slots when it runs on a child.
  const load = useCallback(async () => {
    try {
      // ONE request, and deliberately the tenant-scoped one. `/ai/models/`
      // resolves every family against the PLATFORM, so a tenant that had
      // pinned a family to a newer release saw the stale version here while
      // the AI Models panel directly above showed the current one.
      const res = await api.get(`/super-admin/tenants/${tenantId}/meta-agent-models`);
      setFamilies(res.data?.families || []);
      // Stored refs are used AS THEY ARE. Normalizing a release down to its
      // family here is what made release pins impossible to express.
      setAgents(res.data?.agents || {});
      setCascade(res.data?.cascade || null);
    } catch (err: any) {
      antdMessage.error(`Failed to load meta-agent models: ${err?.message || err}`);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  /** Provider → families → releases. One pass, order preserved. */
  const grouped = useMemo(() => {
    const out: Array<{ provider: string; provider_name: string; families: FamilyEntry[] }> = [];
    const seen: Record<string, number> = {};
    for (const f of families) {
      if (!(f.provider in seen)) {
        seen[f.provider] = out.length;
        out.push({ provider: f.provider, provider_name: f.provider_name || f.provider, families: [] });
      }
      out[seen[f.provider]].families.push(f);
    }
    return out;
  }, [families]);

  /** Every selectable ref: each family key plus every release id. */
  const allRefs = useMemo(
    () => families.flatMap(f => [f.id, ...(f.versions || []).map(v => v.id)]),
    [families],
  );

  const familyFor = (ref: string) =>
    families.find(f => f.id === ref || (f.versions || []).some(v => v.id === ref));

  /** Human label for any ref, family or release. */
  const refLabel = (ref: string): string => {
    const fam = families.find(f => f.id === ref);
    if (fam) return fam.name;
    for (const f of families) {
      const v = (f.versions || []).find(x => x.id === ref);
      if (v) return v.name;
    }
    return ref;
  };

  async function persist(agentKey: string, next: { default_model: string; allowed_models: string[] }) {
    setSavingAgent(agentKey);
    // Keep the pre-optimistic row so a failed PATCH can put it back. Without
    // this the checkbox stayed in its new position after a 400 (the backend
    // rejects a default_model outside allowed_models) and the only signal was
    // a toast — so the tenant looked restricted in the UI while the server
    // still had the old, unrestricted config.
    let rollback: AgentConfig | undefined;
    setAgents(prev => {
      rollback = prev[agentKey];
      return {
        ...prev,
        [agentKey]: {
          ...prev[agentKey],
          default_model: next.default_model || prev[agentKey].platform_default,
          allowed_models: next.allowed_models,
          is_default: !next.default_model,
        },
      };
    });
    try {
      const res = await api.patch(`/super-admin/tenants/${tenantId}/meta-agent-models`, {
        agent: agentKey,
        default_model: next.default_model || null,
        allowed_models: next.allowed_models,
      });
      const d = res.data || {};
      setAgents(prev => ({
        ...prev,
        [agentKey]: {
          ...prev[agentKey],
          default_model: d.default_model ?? prev[agentKey].default_model,
          allowed_models: d.allowed_models ?? next.allowed_models,
          is_default: d.is_default ?? !next.default_model,
        },
      }));
      // The same two toasts the Model families tab shows after a direct edit.
      const cleared: string[] = d.cleared_ancestor_enforcement || [];
      const pushed: number = d.descendants_updated || 0;
      if (cleared.length > 0) {
        antdMessage.warning(t('superAdmin.metaAgentModels.clearedAncestors'));
      }
      if (pushed > 0) {
        antdMessage.info(
          t('superAdmin.metaAgentModels.pushedToDescendants').replace('{count}', String(pushed)),
        );
      }
      // Drift vs the parent moved with this edit; refresh the toolbar badges
      // from the source of truth and let the page refresh its tree badges.
      try {
        const fresh = await api.get(`/super-admin/tenants/${tenantId}/meta-agent-models`);
        setCascade(fresh.data?.cascade || null);
      } catch {
        // The edit itself succeeded; a stale badge is the only cost.
      }
      onCascadeChanged?.();
    } catch (err: any) {
      if (rollback) {
        const previous = rollback;
        setAgents(prev => ({ ...prev, [agentKey]: previous }));
      }
      antdMessage.error(err?.response?.data?.error || 'Failed to save');
    } finally {
      setSavingAgent(null);
    }
  }

  /**
   * Is this ref currently permitted for the agent?
   *
   * An empty allowed list means everything. A FAMILY entry also permits every
   * release inside it — the same rule the backend applies at run time, so the
   * checkbox and the behaviour cannot disagree.
   */
  const isAllowed = (a: AgentConfig, ref: string): boolean => {
    if (a.allowed_models.length === 0) return true;
    if (a.allowed_models.includes(ref)) return true;
    const fam = familyFor(ref);
    return !!fam && fam.id !== ref && a.allowed_models.includes(fam.id);
  };

  function onDefaultChange(agentKey: string, value: string) {
    const a = agents[agentKey];
    persist(agentKey, { default_model: value, allowed_models: a.allowed_models });
  }

  function onToggleAllowed(agentKey: string, ref: string, checked: boolean) {
    const a = agents[agentKey];
    // An empty list means "everything"; the moment one box is unticked it has
    // to become the explicit list minus that entry, or unticking would read as
    // a no-op.
    const current = a.allowed_models.length === 0 ? [...allRefs] : [...a.allowed_models];
    let nextAllowed = checked
      ? Array.from(new Set([...current, ref]))
      : current.filter(x => x !== ref);

    // Unticking a release that was only permitted via its family has to drop
    // the family too, and re-add its siblings — otherwise the family entry
    // would silently keep granting the one just removed.
    if (!checked) {
      const fam = familyFor(ref);
      if (fam && fam.id !== ref && nextAllowed.includes(fam.id)) {
        nextAllowed = nextAllowed.filter(x => x !== fam.id);
        for (const v of fam.versions || []) {
          if (v.id !== ref && !nextAllowed.includes(v.id)) nextAllowed.push(v.id);
        }
      }
    }

    // Everything ticked collapses back to [] so future models are included.
    if (allRefs.every(r => nextAllowed.includes(r))) nextAllowed = [];

    // Keep the default valid: if it falls outside a non-empty list, move it
    // to the first thing that is still permitted.
    let nextDefault = a.is_default ? '' : a.default_model;
    if (nextAllowed.length > 0 && nextDefault) {
      const stillOk = nextAllowed.includes(nextDefault)
        || (familyFor(nextDefault) && nextAllowed.includes(familyFor(nextDefault)!.id));
      if (!stillOk) nextDefault = nextAllowed[0];
    }
    persist(agentKey, { default_model: nextDefault, allowed_models: nextAllowed });
  }

  if (loading) return <Spin />;
  if (families.length === 0) return <Empty description={t('metaAgent.allowedModels.noPlatformModels')} />;

  return (
    <div className="mam">
      <div className="mam-cascade" data-testid="meta-agent-cascade">
        <CascadeControls
          tenantId={tenantId}
          domain="meta_agents"
          cascade={cascade}
          onChanged={() => { load(); onCascadeChanged?.(); }}
        />
      </div>

      <p className="mam-intro">
        {t('superAdmin.metaAgentModels.description')
          || 'Set the default model and which models users can pick for the Concierge and Agent-of-Agents on this tenant. Changes apply on the next message.'}
      </p>

      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        {AGENT_ORDER.map(agentKey => {
          const a = agents[agentKey];
          if (!a) return null;
          const allChecked = a.allowed_models.length === 0;


          return (
            <section key={agentKey} className="mam-agent" data-testid={`meta-agent-${agentKey}`}>
              <header className="mam-agent-head">
                <span className="mam-agent-name" dir="auto">{a.label}</span>
                {a.is_default && (
                  <Tag>{t('superAdmin.metaAgentModels.usingDefault') || 'platform default'}</Tag>
                )}
              </header>

              {/* ── Default ────────────────────────────────────────── */}
              <div className="mam-row">
                <span className="mam-row-label">
                  {t('superAdmin.metaAgentModels.defaultLabel') || 'Default model'}
                </span>
                <Select
                  size="small"
                  className="mam-select"
                  value={a.is_default ? '' : a.default_model}
                  loading={savingAgent === agentKey}
                  onChange={v => onDefaultChange(agentKey, v)}
                  data-testid={`meta-agent-default-${agentKey}`}
                  // Grouped so a release is read in the context of its family
                  // rather than as one of 28 flat names.
                  options={[
                    {
                      value: '',
                      label: `${t('superAdmin.metaAgentModels.platformDefault') || 'Platform default'} (${refLabel(a.platform_default)})`,
                    },
                    ...families
                      .filter(f => allChecked || isAllowed(a, f.id) || (f.versions || []).some(v => isAllowed(a, v.id)))
                      .map(f => ({
                        label: `${f.provider_name || f.provider} · ${f.name}`,
                        options: [
                          {
                            value: f.id,
                            label: `${f.name} — follows the pin (${f.resolved_model_name || f.resolved_model})`,
                            disabled: !isAllowed(a, f.id),
                          },
                          ...(f.versions || [])
                            .map(v => ({
                              value: v.id,
                              label: v.is_family_pin ? `${v.name} (current pin)` : v.name,
                              disabled: !isAllowed(a, v.id),
                            })),
                        ],
                      })),
                  ]}
                />
              </div>

              {/* ── Allowed ────────────────────────────────────────── */}
              <div className="mam-allowed-label">
                {t('superAdmin.metaAgentModels.allowedLabel') || 'Models users can pick'}
                {allChecked && (
                  <span className="mam-allowed-all">
                    {t('superAdmin.metaAgentModels.allAllowed') || 'all models (default)'}
                  </span>
                )}
              </div>

              <div className="mam-groups">
                {grouped.map(group => (
                  <div key={group.provider} className="mam-provider">
                    <div className="mam-provider-name">{group.provider_name}</div>
                    {group.families.map(f => (
                      <div key={f.id} className="mam-family-block">
                        <Checkbox
                          className="mam-family-check"
                          checked={isAllowed(a, f.id) && (allChecked || a.allowed_models.includes(f.id))}
                          disabled={savingAgent === agentKey}
                          onChange={e => onToggleAllowed(agentKey, f.id, e.target.checked)}
                          data-testid={`meta-allow-${agentKey}-${f.id}`}
                        >
                          <span className="mam-family">
                            <span className="mam-family-name" dir="auto">{f.name}</span>
                            <span className="mam-family-version">
                              follows the pin · {f.resolved_model_name || f.resolved_model}
                            </span>
                            {f.enabled === false && <Tag className="mam-family-off">off</Tag>}
                          </span>
                        </Checkbox>

                        {/* Every release in the family — the same list the
                            Model families tab offers — folded away by default
                            so the section stays a screen rather than three.
                            Opened automatically when one of them is ticked
                            individually: a selection you cannot see is worse
                            than a long list. */}
                        {(f.versions || []).length > 0 && (() => {
                          const pinnedHere = (f.versions || []).some(
                            v => a.allowed_models.includes(v.id),
                          );
                          const open = openFamilies.has(`${agentKey}:${f.id}`) || pinnedHere;
                          return (
                            <>
                              <button
                                type="button"
                                className="mam-disclosure"
                                aria-expanded={open}
                                onClick={() => toggleFamilyOpen(agentKey, f.id)}
                                data-testid={`meta-releases-toggle-${agentKey}-${f.id}`}
                              >
                                {open ? '▾' : '▸'}{' '}
                                {open
                                  ? t('metaAgent.allowedModels.hideReleases')
                                  : t('metaAgent.allowedModels.pickExactRelease', { count: (f.versions || []).length })}
                              </button>
                              {open && (
                                <div className="mam-releases">
                                  {(f.versions || []).map(v => (
                                    <Checkbox
                                      key={v.id}
                                      checked={isAllowed(a, v.id)}
                                      disabled={savingAgent === agentKey}
                                      onChange={e => onToggleAllowed(agentKey, v.id, e.target.checked)}
                                      data-testid={`meta-allow-${agentKey}-${v.id}`}
                                    >
                                      <span className="mam-release">
                                        <span className="mam-release-name" dir="auto">{v.name}</span>
                                        <span className="mam-release-id">{v.id}</span>
                                        {v.is_family_pin && (
                                          <Tag className="mam-release-pin">{t('superAdmin.wl.currentPin')}</Tag>
                                        )}
                                      </span>
                                    </Checkbox>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </Space>
    </div>
  );
}

export default MetaAgentModelsPanel;
